import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { agent } from "./agent/runtime.js";
import type { AgentState } from "./agent/agentService.js";
import type { TrustTier } from "@alinq/proto";

const TIER_LABEL: Record<TrustTier, string> = { 0: "Connection", 1: "Friend", 2: "Business", 3: "Financial" };
const TIER_EXPLAIN: Record<TrustTier, string> = {
  0: "Just your card — a name and one way to reach you.",
  1: "Availability, introductions and private scheduling.",
  2: "Business availability, quotes and referrals.",
  3: "Can act on your behalf, within limits + a second factor.",
};
const TIERS: TrustTier[] = [0, 1, 2, 3];

export function TierBadge({ tier }: { tier: TrustTier }) {
  return <span className={`tier-badge t${tier}`}>{tier} · {TIER_LABEL[tier]}</span>;
}

function TierSelect({ value, onChange }: { value: TrustTier; onChange: (t: TrustTier) => void }) {
  return (
    <>
      <select value={value} onChange={(e) => onChange(Number(e.target.value) as TrustTier)}>
        {TIERS.map((t) => <option key={t} value={t}>{t} · {TIER_LABEL[t]}</option>)}
      </select>
      <p className="hint">{TIER_EXPLAIN[value]}</p>
    </>
  );
}

function useAsyncAction(): [string | undefined, boolean, (fn: () => Promise<void> | void) => Promise<void>] {
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void> | void) => {
    setError(undefined);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return [error, busy, run];
}

async function shareOrCopy(text: string): Promise<string> {
  const nav = navigator as Navigator & { share?: (d: { text: string }) => Promise<void> };
  if (nav.share) {
    try { await nav.share({ text }); return "Shared"; } catch { /* cancelled */ return ""; }
  }
  await navigator.clipboard.writeText(text);
  return "Copied to clipboard";
}

export function Onboarding() {
  const [username, setUsername] = useState("");
  const [error, busy, run] = useAsyncAction();
  return (
    <div className="onb">
      <img className="lockup" src="/brand/alinq-lockup.png?v=2" alt="Alinq" />
      <h1>Connect your agent</h1>
      <p className="intro">
        Alinq is how your AI agent securely connects and communicates with other people's
        agents — your agent's gateway to the world. Your address is the handle you share so
        others can reach it.
      </p>
      <div className="reassure">🔒 Your keys are created on this device and never leave it.</div>
      <div className="panel" style={{ textAlign: "left", marginTop: 18 }}>
        <label htmlFor="u">Choose a username</label>
        {/* Usernames are lowercase by protocol; iOS keyboards love to capitalize the
            first letter anyway, so normalize while typing instead of rejecting. */}
        <input id="u" value={username} placeholder="e.g. sam" autoCapitalize="none"
          autoCorrect="off" spellCheck={false} inputMode="email"
          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ""))} />
        <p className="hint">a–z, 0–9, dot or underscore · 3–30 characters. A unique tag is added so your handle can't be hijacked.</p>
        <button className="btn btn-primary btn-block" disabled={busy || username.length < 3}
          onClick={() => void run(async () => { await agent.onboard(username); })} style={{ marginTop: 14 }}>
          {busy ? "Generating your keys…" : "Create my Alinq address"}
        </button>
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}

export function HowToLink() {
  return (
    <div className="panel guide">
      <h2>🔗 Link with another agent</h2>
      <ol>
        <li><b>Share your address + a pairing code</b> (below) with the person you want to connect with.</li>
        <li>They paste it into their <b>Connect</b> tab — a request then lands in your <b>Requests</b> tab.</li>
        <li>You <b>Accept</b> it, choosing a trust level. You're linked — now your agents can collaborate (e.g. find a meeting time).</li>
      </ol>
      <div className="alt">Already have someone's address + code? Open the <b>Connect</b> tab to link from your side instead.</div>
    </div>
  );
}

export function MyAddress({ state }: { state: AgentState }) {
  const [qr, setQr] = useState<string>();
  const [tier, setTier] = useState<TrustTier>(1);
  const [note, setNote] = useState<string>();
  const invite = agent.myInviteLink();

  useEffect(() => {
    if (invite) void QRCode.toDataURL(invite, { margin: 1, width: 220, color: { dark: "#010721", light: "#ffffff" } }).then(setQr);
  }, [invite]);

  return (
    <div>
      <div className="panel address-hero">
        <h2 style={{ textAlign: "center" }}>Your Alinq address</h2>
        <div className="address gradient-text">{state.handle}</div>
        <div className="did">{state.did}</div>
        {qr && <div className="qr-card"><img src={qr} alt="Your address as a QR code" /></div>}
        <div className="btnrow" style={{ marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={() => void shareOrCopy(state.handle ?? "").then(setNote)}>Copy address</button>
          <button className="btn btn-primary" onClick={() => void shareOrCopy(invite ?? state.handle ?? "").then(setNote)}>Share</button>
        </div>
        {note && <div className="success" style={{ textAlign: "center" }}>{note}</div>}
        <p className="hint" style={{ textAlign: "center" }}>Sharing your address is only an invitation — nobody connects until you accept a link.</p>
      </div>

      <HowToLink />

      <div className="panel">
        <h2>Pairing code</h2>
        <p className="hint">Give this code to the person you want to connect with. It controls who may link, and at what trust level.</p>
        <label>This code grants the level</label>
        <TierSelect value={tier} onChange={setTier} />
        <button className="btn btn-secondary btn-block" onClick={() => agent.generatePairingCode(tier)} style={{ marginTop: 12 }}>
          Generate a pairing code
        </button>
        {state.pairingCode && (
          <div className="code-wrap">
            <div className="code gradient-text">{state.pairingCode.code}</div>
            <button className="link" onClick={() => void shareOrCopy(state.pairingCode!.inviteUrl).then(setNote)}>Copy invite link</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Connect({ initialTarget }: { initialTarget?: string }) {
  const [target, setTarget] = useState(initialTarget ?? "");
  const [code, setCode] = useState("");
  const [tier, setTier] = useState<TrustTier>(1);
  const [sent, setSent] = useState(false);
  const [error, busy, run] = useAsyncAction();
  return (
    <div className="panel">
      <h2>Connect to an agent</h2>
      <p className="hint">Paste someone's handle or invite link, then the pairing code they gave you. (Scanning a QR and matching contacts arrive with the native app.)</p>
      <label>Handle or invite link</label>
      <input value={target} placeholder="@mara#A19C" autoCapitalize="none" autoCorrect="off" onChange={(e) => setTarget(e.target.value)} />
      <label>Pairing code</label>
      <input value={code} placeholder="ABCD2345" autoCapitalize="characters" autoCorrect="off" onChange={(e) => setCode(e.target.value)} />
      <label>Trust level you're requesting</label>
      <TierSelect value={tier} onChange={setTier} />
      <button className="btn btn-primary btn-block" disabled={busy || target.length < 3} style={{ marginTop: 14 }}
        onClick={() => void run(async () => { await agent.connect(target, code, tier); setSent(true); })}>
        {busy ? "Sending link request…" : "Send link request"}
      </button>
      {sent && <div className="success">Request sent. It stays pending until the other agent accepts.</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export function Requests({ state }: { state: AgentState }) {
  if (state.requests.length === 0) {
    return <div className="empty"><span className="big">📭</span>No incoming requests.<br />When someone asks to connect, it shows up here.</div>;
  }
  return (
    <div className="panel">
      <h2>Incoming requests</h2>
      {state.requests.map((r) => (
        <div className="list-item" key={r.did} style={{ flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 100%" }}>
            <div className="peer-name">{r.handle}</div>
            <div className="peer-sub">wants to connect as <strong>{TIER_LABEL[r.requestedTier]}</strong> — {TIER_EXPLAIN[r.requestedTier]}</div>
          </div>
          <div className="btnrow" style={{ width: "100%", marginTop: 10 }}>
            <button className="btn btn-secondary" onClick={() => agent.declineRequest(r.did)}>Decline</button>
            <button className="btn btn-primary" onClick={() => agent.acceptRequest(r.did)}>Accept</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Network({ state }: { state: AgentState }) {
  if (state.peers.length === 0) {
    return (
      <div>
        <div className="empty"><span className="big">🌐</span>No connections yet — here's how to make your first one:</div>
        <HowToLink />
      </div>
    );
  }
  return (
    <div className="panel">
      <h2>Your network</h2>
      {state.peers.map((p) => (
        <div className="list-item" key={p.did}>
          <div>
            <div className="peer-name">{p.handle}</div>
            <div className="peer-sub">{p.sessionOpen ? "secure session open" : "no active session"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flex: "0 0 auto" }}>
            <TierBadge tier={p.tier} />
            {p.tier > 0 && (
              <button className="link danger" onClick={() => {
                if (confirm(`Revoke your connection with ${p.handle}? This takes effect immediately.`)) agent.downgrade(p.did, 0);
              }}>Revoke</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Activity({ state }: { state: AgentState }) {
  const [slotInput, setSlotInput] = useState("2026-07-01T09:00, 2026-07-01T11:00, 2026-07-01T14:00");
  const [peerDid, setPeerDid] = useState(state.peers.find((p) => p.tier >= 1)?.did ?? "");
  const [error, busy, run] = useAsyncAction();
  const friends = state.peers.filter((p) => p.tier >= 1);

  return (
    <div>
      <div className="panel">
        <h2>Zero-knowledge scheduling</h2>
        <p className="hint">Find a common free slot with a Friend — without either of you sharing your calendar.</p>
        <label>My free slots (comma-separated)</label>
        <input value={slotInput} onChange={(e) => setSlotInput(e.target.value)} />
        <button className="btn btn-secondary btn-block" style={{ marginTop: 10 }}
          onClick={() => agent.setAvailability(slotInput.split(",").map((s) => s.trim()).filter(Boolean))}>
          Save my availability
        </button>
        <label>Match with</label>
        <select value={peerDid} onChange={(e) => setPeerDid(e.target.value)}>
          {friends.length === 0 && <option value="">No Friend-tier connections yet</option>}
          {friends.map((p) => <option key={p.did} value={p.did}>{p.handle}</option>)}
        </select>
        <button className="btn btn-primary btn-block" disabled={!peerDid || busy} style={{ marginTop: 12 }}
          onClick={() => void run(() => agent.scheduleWith(peerDid).then(() => undefined))}>
          {busy ? "Matching privately…" : "Find a common slot"}
        </button>
        {error && <div className="error">{error}</div>}
      </div>
      <div className="panel">
        <h2>What your agent did</h2>
        {state.activity.length === 0
          ? <div className="empty">Nothing yet — your agent's actions will appear here.</div>
          : <div className="activity">{state.activity.map((a, i) => (
              <div className="item" key={i}><span className="spark">✦</span><span>{a.text}</span></div>
            ))}</div>}
      </div>
    </div>
  );
}
